// App state machine router — HANDOFF schema Section 10.
// States: ID_ENTRY, SCHEDULE_SETUP, DASHBOARD, PRE_SURVEY, TEXT_RECORDING,
// IMAGE_RECORDING, POST_SURVEY, M2C2_BATTERY, SESSION_COMPLETE,
// RESEARCHER_PANEL — exactly one active at all times.

import { useEffect, useState } from 'react';
import { useParticipantStore } from './store/participantStore';
import { useSessionStore } from './store/sessionStore';
import { requestPersistentStorage, checkStorageQuota } from './db/indexedDB';
import { listenForForegroundMessages, refreshPushRegistration } from './notifications/fcm';
import { unparkSession, readBatteryResults, clearBatteryResults } from './utils/m2c2Handoff';
import { logLine } from './utils/m2c2Log';

import IDEntryScreen from './screens/IDEntryScreen';
import ScheduleSetupScreen from './screens/ScheduleSetupScreen';
import DashboardScreen from './screens/DashboardScreen';
import PreSurveyScreen from './screens/PreSurveyScreen';
import TextRecordingScreen from './screens/TextRecordingScreen';
import ImageRecordingScreen from './screens/ImageRecordingScreen';
import PostSurveyScreen from './screens/PostSurveyScreen';
import M2C2BatteryScreen from './screens/M2C2BatteryScreen';
import SessionCompleteScreen from './screens/SessionCompleteScreen';
import ResearcherPanelScreen from './screens/ResearcherPanelScreen';
import DiagnosticsScreen from './screens/DiagnosticsScreen';

const STATES = {
  ID_ENTRY: 'ID_ENTRY',
  SCHEDULE_SETUP: 'SCHEDULE_SETUP',
  DASHBOARD: 'DASHBOARD',
  PRE_SURVEY: 'PRE_SURVEY',
  TEXT_RECORDING: 'TEXT_RECORDING',
  IMAGE_RECORDING: 'IMAGE_RECORDING',
  POST_SURVEY: 'POST_SURVEY',
  M2C2_BATTERY: 'M2C2_BATTERY',
  SESSION_COMPLETE: 'SESSION_COMPLETE',
  RESEARCHER_PANEL: 'RESEARCHER_PANEL',
  // Not part of the participant flow — reads back the crash-surviving M2C2
  // log after a freeze. Deliberately NOT PIN-gated: it contains no
  // participant data, and gating it behind verifyPin() would make it depend
  // on the network at exactly the moment something is already going wrong.
  DIAGNOSTICS: 'DIAGNOSTICS',
};

export default function App() {
  const hydrate = useParticipantStore((s) => s.hydrate);
  const loaded = useParticipantStore((s) => s.loaded);
  const participantId = useParticipantStore((s) => s.participant_id);
  const schedule = useParticipantStore((s) => s.schedule);

  const restoreSession = useSessionStore((s) => s.restore);

  const [state, setState] = useState(STATES.ID_ENTRY);
  const [storageWarning, setStorageWarning] = useState(false);
  // The M2C2 battery runs as separate page loads, so returning from it is a
  // cold app boot, not a state transition. Until the parked session has been
  // read back from IndexedDB we must not let the normal routing effect below
  // send the participant to the Dashboard — that would look like their
  // session had vanished.
  const [resuming, setResuming] = useState(
    () => new URLSearchParams(window.location.search).get('m2c2') === 'done'
  );

  useEffect(() => {
    hydrate();
    requestPersistentStorage();
    checkStorageQuota().then(({ ratio }) => setStorageWarning(ratio > 0.8));
    listenForForegroundMessages();

    if (window.location.hash === '#researcher') {
      setState(STATES.RESEARCHER_PANEL);
    } else if (window.location.hash === '#m2c2log') {
      setState(STATES.DIAGNOSTICS);
    }
  }, [hydrate]);

  // Coming back from the M2C2 battery. The assessments ran as their own pages
  // (see utils/m2c2Handoff.js), so everything the session held in memory is
  // gone and has to be read back before we can continue to SESSION_COMPLETE.
  useEffect(() => {
    if (!resuming || !loaded) return;
    let cancelled = false;
    (async () => {
      try {
        const sessionId = useParticipantStore.getState().current_session_id;
        if (!sessionId) throw new Error('no session id on participant state');

        const snapshot = await unparkSession(sessionId);
        if (!snapshot) throw new Error(`no parked session for ${sessionId}`);

        const results = readBatteryResults(sessionId);
        const done = results
          ? Object.values(results.activities).filter((a) => a.trials.length > 0).length
          : 0;
        logLine(`resumed session ${sessionId} — ${done}/4 activities returned data`);

        if (cancelled) return;
        restoreSession({
          ...snapshot,
          m2c2Results: results ?? { battery_complete: false, activities: {} },
        });
        clearBatteryResults();
        setState(STATES.SESSION_COMPLETE);
      } catch (err) {
        // Falling through to the Dashboard is the safe failure here: the
        // parked record and its audio are still in IndexedDB either way, and
        // the Dashboard's "something went wrong" reset can clear the flag.
        logLine(`ERROR resuming after battery: ${String(err)}`);
        setState(STATES.DASHBOARD);
      } finally {
        if (!cancelled) {
          window.history.replaceState({}, '', '/');
          setResuming(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resuming, loaded]);

  // Re-claim push registration for this device once the participant state is
  // known. See refreshPushRegistration() — the server keeps one token per
  // participant, and nothing else re-registers after initial setup.
  useEffect(() => {
    if (!loaded || !participantId || !schedule) return;
    refreshPushRegistration({
      participantId,
      schedule,
      currentToken: useParticipantStore.getState().fcm_token,
      onToken: (token) =>
        useParticipantStore.getState().patch({ fcm_token: token, notifications_enabled: true }),
    });
  }, [loaded, participantId, schedule]);

  useEffect(() => {
    if (!loaded) return;
    if (resuming) return; // the resume effect above owns routing until it's done
    if (state !== STATES.ID_ENTRY) return; // don't clobber a screen the user is already on
    if (!participantId) {
      setState(STATES.ID_ENTRY);
    } else if (!schedule) {
      setState(STATES.SCHEDULE_SETUP);
    } else {
      setState(STATES.DASHBOARD);
    }
  }, [loaded, participantId, schedule, state, resuming]);

  if (!loaded || resuming) {
    return (
      <div className="min-h-screen flex items-center justify-center text-dim">
        {resuming ? 'Saving your activities…' : 'Loading…'}
      </div>
    );
  }

  return (
    <>
      {storageWarning && (
        <div className="bg-amber/10 border-b border-amber/30 text-amber text-sm text-center py-2 px-4">
          Storage is running low. Please upload on Wi-Fi before continuing.
        </div>
      )}

      {state === STATES.ID_ENTRY && (
        <IDEntryScreen onDone={() => setState(schedule ? STATES.DASHBOARD : STATES.SCHEDULE_SETUP)} />
      )}

      {state === STATES.SCHEDULE_SETUP && <ScheduleSetupScreen onDone={() => setState(STATES.DASHBOARD)} />}

      {state === STATES.DASHBOARD && (
        <DashboardScreen
          onBeginSession={() => setState(STATES.PRE_SURVEY)}
          onOpenResearcherPanel={() => setState(STATES.RESEARCHER_PANEL)}
          onOpenDiagnostics={() => setState(STATES.DIAGNOSTICS)}
        />
      )}

      {state === STATES.PRE_SURVEY && (
        <PreSurveyScreen
          onContinue={() => setState(STATES.TEXT_RECORDING)}
          onAbandon={() => setState(STATES.DASHBOARD)}
        />
      )}

      {state === STATES.TEXT_RECORDING && (
        <TextRecordingScreen
          onContinue={() => setState(STATES.IMAGE_RECORDING)}
          onAbandon={() => setState(STATES.DASHBOARD)}
        />
      )}

      {state === STATES.IMAGE_RECORDING && (
        <ImageRecordingScreen
          onContinue={() => setState(STATES.POST_SURVEY)}
          onAbandon={() => setState(STATES.DASHBOARD)}
        />
      )}

      {state === STATES.POST_SURVEY && (
        <PostSurveyScreen
          onContinue={() => setState(STATES.M2C2_BATTERY)}
          onAbandon={() => setState(STATES.DASHBOARD)}
        />
      )}

      {state === STATES.M2C2_BATTERY && (
        <M2C2BatteryScreen onContinue={() => setState(STATES.SESSION_COMPLETE)} />
      )}

      {state === STATES.SESSION_COMPLETE && (
        <SessionCompleteScreen onDone={() => setState(STATES.DASHBOARD)} />
      )}

      {state === STATES.RESEARCHER_PANEL && (
        <ResearcherPanelScreen
          onClose={() => {
            window.location.hash = '';
            // Switching participant clears the schedule, so going straight to
            // the Dashboard would land on a screen with no reminder times and
            // no way to set them. Route to schedule setup instead.
            setState(
              useParticipantStore.getState().schedule ? STATES.DASHBOARD : STATES.SCHEDULE_SETUP
            );
          }}
        />
      )}

      {state === STATES.DIAGNOSTICS && (
        <DiagnosticsScreen
          onClose={() => {
            window.location.hash = '';
            setState(STATES.DASHBOARD);
          }}
        />
      )}
    </>
  );
}
